<script setup lang="ts">
import { computed, ref } from 'vue';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useField from '@/composables/useField';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export type FieldSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ItemProps {
  subtitle?: string;
  disabled?: boolean;
  titleIcon?: string;
}

export interface Item {
  value: boolean | string | number;
  title: string;
  props?: ItemProps;
}

export interface Props {
  modelValue?: boolean | string | number | null;
  size?: FieldSize;
  items?: Item[];
  clearable?: boolean;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  readonly?: boolean;
  rules?: ((v: boolean | string | number | null) => boolean | string)[];
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  items: () => [],
  clearable: true,
  label: '',
  disabled: false,
  loading: false,
  readonly: false,
  rules: () => [],
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-select
    v-model="fieldValue"
    :items="props.items"
    :label="props.label"
    :disabled="props.disabled"
    :loading="props.loading"
    :readonly="props.readonly"
    :rules="props.rules"
    :class="['g-v-text-field', $style.kSelectField, fieldStyles[sizeClass]]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :clearable="props.readonly ? false : props.clearable"
    :menu-icon="props.readonly ? '' : 'mdi-chevron-down'"
    auto-select-first
    data-testid="kSelectField"
  >
    <template #selection="{ item }">
      <slot name="selection" :item="item">
        <span class="v-select__selection-text">{{ item.title }}</span>
      </slot>
    </template>
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        v-bind="itemData.props"
      >
        <template #title="{ title }">
          <v-icon
            v-if="itemData.item.props.titleIcon"
            :class="listItemStyles.titleIcon"
            :icon="itemData.item.props.titleIcon"
          >
          </v-icon>
          {{ title }}
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.kSelectField {
  :global {
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}
</style>
